import { ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { Link, useSearchParams } from 'react-router';
import { round } from 'lodash-es';
import { PaymentsFilters } from '@/components/payments/payments-filters';
import { ActionIcon, CopyButton, Flex, NumberFormatter, Popover, Stack, Tooltip, Button, Group } from '@mantine/core';
import { IconCopy, IconChecks, IconReload } from '@tabler/icons-react';
import { paymentsControllerList, PaymentsListResponseDtoRowsItem } from '@/api-client/api';
import { LoadingError } from '@/common/loading-error';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { formatDateTime, declMonth } from '@admin-panel/shared';

const intlRubles = new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB" });

const PAGE_SIZES = [ 50, 100, 200, 500 ];

export function PaymentsList(): ReactNode {
    const [ searchParams, setSearchParams ] = useSearchParams();

    const { isLoading, error, data, refetch } = useQuery({
        queryKey: [
            'payments',
            searchParams.get('sort'),
            searchParams.get('direction'),
            searchParams.get('page'),
            searchParams.get('limit'),
            searchParams.get('search'),
        ],
        queryFn: () => paymentsControllerList({
            page: +(searchParams.get('page') || '1'),
            limit: +(searchParams.get('limit') || '50'),
            sort: searchParams.get('sort') || 'createdAt',
            direction: searchParams.get('direction') as 'asc' | 'desc' || 'desc',
            search: searchParams.get('search') || '',
        }),
        placeholderData: keepPreviousData,
    });

    const columns: DataTableColumn<PaymentsListResponseDtoRowsItem>[] = [
        {
            accessor: 'id',
            title: 'ID',
        },
        {
            accessor: 'tgUserId',
            title: 'Пользователь',
            render(row) {
                return (
                    <Link to={ `/users/?search=${ row.user.tgUserId || row.user.email }` }>
                        { row.user.tgUserId || row.user.email }
                    </Link>
                )
            }
        },
        {
            accessor: 'createdAt',
            title: 'Дата платежа',
            render(row) {
                return formatDateTime(row.createdAt);
            }
        },
        {
            accessor: 'tariff',
            title: 'Тариф',
            render(row) {
                return declMonth(row.tariff);
            }
        },
        {
            accessor: 'paymentOrder.paymentType',
            title: 'Вид оплаты',
            render(row) {
                return row.paymentOrder.paymentType;
            }
        },
        {
            accessor: 'Факт. сумма',
            title: 'payedAmount',
            render(row) {
                return (
                    <NumberFormatter
                        value={ row.payedAmount }
                        suffix=' ₽'
                        decimalScale={ 2 }
                        fixedDecimalScale
                        thousandSeparator=' '
                    />
                );
            }
        },
        {
            accessor: 'Сумма',
            title: 'payedAmount',
            render(row) {
                const paidAt = DateTime.fromISO(row.paymentOrder.createdAt);
                const paidTo = paidAt.plus({ months: row.tariff });
                const daysDiff = paidTo.diff(paidAt, 'days').days;
                let leftDays = 0;
                let leftAmount = 0;
                let dayCost = 0;

                if (paidTo > DateTime.now()) {
                    leftDays = paidTo.diffNow('days').days;
                    dayCost = row.orderAmount / daysDiff;
                    leftAmount = leftDays * dayCost;
                }

                return (
                    <Group gap='xs'>
                        <NumberFormatter
                            value={ row.orderAmount }
                            suffix=' ₽'
                            decimalScale={ 2 }
                            fixedDecimalScale
                            thousandSeparator=' '
                        />
                        { leftDays && (
                            <Popover width={ 280 } position='bottom' withArrow shadow='md'>
                                <Popover.Target>
                                    <Button variant='outline' color='dark' size='xs'>Возврат</Button>
                                </Popover.Target>
                                <Popover.Dropdown>
                                    <Stack>
                                        <div>Дней подписки: { daysDiff } </div>
                                        <div>Осталось дней: { Math.round(leftDays) } </div>
                                        <div>Стоимость дня: { intlRubles.format(round(dayCost, 2)) } / день</div>
                                        <div>Сумма к возврату: { intlRubles.format(round(leftAmount)) } </div>
                                    </Stack>
                                </Popover.Dropdown>
                            </Popover>
                        ) }
                    </Group>
                )
            }
        },
        {
            accessor: 'paymentOrder.merchantOrderId',
            title: 'Merchant order ID',
            render(row) {
                return (
                    <Group gap='xs'>
                        <Tooltip label={ row.paymentOrder.merchantOrderId }>
                            <span
                                style={ {
                                    display: 'inline-block',
                                    maxWidth: 260,
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                } }
                            >{ row.paymentOrder.merchantOrderId }</span>
                        </Tooltip>
                        { row.paymentOrder.merchantOrderId && (
                            <CopyButton value={ row.paymentOrder.merchantOrderId } timeout={ 2000 }>
                                { ({ copied, copy }) => (
                                    <Tooltip label={ copied ? 'Copied' : 'CopyIcon' } withArrow position='right'>
                                        <ActionIcon color={ copied ? 'teal' : 'gray' } variant='subtle'
                                                    onClick={ copy }>
                                            { copied ? <IconChecks size={ 16 }/> : <IconCopy size={ 16 }/> }
                                        </ActionIcon>
                                    </Tooltip>
                                ) }
                            </CopyButton>
                        ) }
                    </Group>
                )
            }
        },

        {
            accessor: 'paymentOrder.merchant',
            title: 'Merchant',
            render(row) {
                return row.paymentOrder.merchant;
            },
        },
        {
            accessor: 'rate',
            title: 'Rate',
            render(row) {
                return row.user.personalPaymentRate;
            },
        },
    ];

    if (error) return <LoadingError error={ error }/>;

    const limit = parseInt(searchParams.get('limit') || '50');

    return (
        <Stack>
            <Flex align='center' gap={ 7 }>
                <PaymentsFilters/>
                <Button
                    onClick={ () => refetch() }
                    color='gray'
                >
                    <IconReload size={14}/>
                </Button>
            </Flex>
            <DataTable
                idAccessor='id'
                columns={ columns }
                records={ (data?.rows || []) }
                totalRecords={ data?.total || 0 }
                page={ parseInt(searchParams.get('page') || '1') }
                recordsPerPage={ limit }
                recordsPerPageOptions={ PAGE_SIZES }
                onPageChange={ (nextPage) => {
                    setSearchParams((prev) => {
                        prev.set('page', String(nextPage));
                        return prev;
                    });
                } }
                onRecordsPerPageChange={ (limit) => {
                    setSearchParams((prev) => {
                        prev.set('limit', String(limit));
                        return prev;
                    });
                } }
                sortStatus={ {
                    columnAccessor: searchParams.get('sort') || 'createdAt',
                    direction: (searchParams.get('direction') as 'asc' | 'desc') || 'desc',
                } }
                onSortStatusChange={ ({ columnAccessor, direction }) => {
                    setSearchParams((prev) => {
                        prev.set('sort', columnAccessor);
                        prev.set('direction', direction);
                        return prev;
                    });
                } }
                fetching={ isLoading }
                loaderSize='sm'
                minHeight={ 150 }
                loaderBackgroundBlur={ 4 }
                pinFirstColumn
                highlightOnHover
            />
        </Stack>
    );
}